
import { defineComponent, reactive, onBeforeMount, computed } from 'vue'
import { useRoute } from 'vue-router'

// components
import FtlBidState from '@/modules/FTLBidsModule/components/FtlBidState/index.vue'
import FtlBidHeader from '@/modules/FTLBidsModule/components/FtlBidHeader/index.vue'
import FtlBidTabs from '@/modules/FTLBidsModule/components/FtlBidTabs/index.vue'
import FtlBidGeneral from '@/modules/FTLBidsModule/components/FtlBidGeneral/index.vue'
import FtlBidCargoInformation from '@/modules/FTLBidsModule/components/FtlBidCargoInformation/index.vue'
import FtlBidPayment from '@/modules/FTLBidsModule/components/FtlBidPayment/index.vue'
import FtlBidWaypoints from '@/modules/FTLBidsModule/components/FtlBidWaypoints/index.vue'
import FtlBidFiles from '@/modules/FTLBidsModule/components/FtlBidFiles/index.vue'
import FtlBidCar from '@/modules/FTLBidsModule/components/FtlBidCar/index.vue'
import FtlBidStatuses from '@/modules/FTLBidsModule/components/FtlBidStatuses/index.vue'

import EnterCostToBePaid from '@/modules/FTLBidsModule/components/EnterCostToBePaid/index.vue'
import BidsFormFtl from '@/modules/BidsModule/components/BidsFormFtl/index.vue'
import EnterCarrierData from '@/modules/FTLBidsModule/components/EnterCarrierData/index.vue'
import EnterCostPrice from '@/modules/FTLBidsModule/components/EnterCostPrice/index.vue'
import EnterComment from '@/modules/FTLBidsModule/components/EnterComment/index.vue'
import EnterCancelReason from '@/modules/FTLBidsModule/components/EnterCancelReason/index.vue'

// types
import {
    ChangeStatusParamsT,
    EmitCarrierDataParamsT,
    EnterCarrierDataParamsT,
    EnterCostPriceParamsT,
    EnterCostPricePayloadT,
    FTLBidT,
} from '@/core/types/ftl_bids.types'

// realisations
import { ftl_bids } from '@/core/realisations/ftl_bids.realisation'

// composable
import useNotifications from '@/common/composable/useNotifications'
import { contracts } from '@/core/realisations/contracts.realisation'

export default defineComponent({
    components: {
        FtlBidState,
        FtlBidHeader,
        EnterCostToBePaid,
        BidsFormFtl,
        EnterCarrierData,
        EnterCostPrice,
        EnterComment,
        EnterCancelReason,
        FtlBidTabs,
        FtlBidGeneral,
        FtlBidCargoInformation,
        FtlBidPayment,
        FtlBidWaypoints,
        FtlBidFiles,
        FtlBidCar,
        FtlBidStatuses,
    },
    setup() {
        const route = useRoute()
        const notifications = useNotifications()

        const id = Number(route.params.id)

        const state = reactive({
            is_loading: false,
            is_failed: false,
            is_document_loading: false,
            is_download_loading: false,
            ftlBid: null as FTLBidT | null,
            currentTab: 'general',
            is_status_changing: false,
            //
            is_enter_cost_to_be_paid: false,
            is_enter_cost_to_be_paid_loading: false,
            //
            is_create_bid: false,
            is_enter_carrier_data: false,
            is_enter_carrier_data_loading: false,
            //
            is_enter_cost_price: false,
            is_enter_cost_price_loading: false,
            //
            is_enter_comment: false,
            is_enter_comment_loading: false,
            //
            is_cancel_bid: false,
            //
            is_invoices_printing: false,
        })

        const loadFtlBid = async () => {
            try {
                state.is_loading = true

                const response = await ftl_bids.getFTLBid(id)
                state.ftlBid = response.data

                state.is_failed = false
            } catch (error) {
                console.error(error)
                state.is_failed = true

                notifications.notifyError({
                    error,
                    title: 'При получении FTL заявки произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(loadFtlBid)

        const changeStatusTo = async (
            status_code: string,
            rejection_comment?: string,
            rejection_reason_id?: number
        ) => {
            try {
                if (!state.ftlBid) return

                const params: ChangeStatusParamsT = {
                    id: state.ftlBid.id,
                    status_code,
                }

                if (rejection_comment) params.rejection_comment = rejection_comment
                if (rejection_reason_id) params.rejection_reason_id = rejection_reason_id

                state.is_status_changing = true

                await ftl_bids.changeStatus(params)

                notifications.notify({
                    title: 'Статус изменен',
                    type: 'success',
                })
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При изменении статуса произошла ошибка!',
                })
            } finally {
                state.is_status_changing = false
            }
        }

        const handleChangeStatus = async (status_code: string) => {
            const answer = confirm(`Вы подтверждаете смену статуса?`)
            if (!answer) return

            await changeStatusTo(status_code)
            loadFtlBid()
        }

        const onAcceptInWork = () => {
            if (!state.ftlBid) return

            if (state.ftlBid.is_need_car) {
                handleChangeStatus('wait_cost_price')
            } else {
                handleChangeStatus('carrier_data_entered')
            }
        }

        const onEnterCostToBePaid = () => {
            state.is_enter_cost_to_be_paid = true
        }

        const enterCostToBePaidSubmit = async (paid_price: number) => {
            if (!state.ftlBid) return
            if (state.is_enter_cost_to_be_paid_loading) return

            try {
                state.is_enter_cost_to_be_paid_loading = true

                const params = {
                    id: state.ftlBid.id,
                    paid_price: Number(paid_price),
                }

                await ftl_bids.enterCostToBePaid(params)
                await changeStatusTo('approved')

                notifications.notify({
                    title: 'Выставление клиенту внесено',
                    type: 'success',
                })

                state.is_enter_cost_to_be_paid = false

                loadFtlBid()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При внесении суммы к оплате произошла ошибка!',
                })
            } finally {
                state.is_enter_cost_to_be_paid_loading = false
            }
        }

        const enterCostToBePaidCancel = () => {
            state.is_enter_cost_to_be_paid = false
        }

        const onRepeatCalculateCost = () => {
            handleChangeStatus('wait_cost_price')
        }

        const onBidAdded = () => {
            state.is_create_bid = false
            loadFtlBid()
        }

        const onEnterCarrierData = () => {
            if (!state.ftlBid) return

            if ([0, 1].includes(state.ftlBid.ftl_type)) {
                state.is_create_bid = true
            } else {
                state.is_enter_carrier_data = true
            }
        }

        const getCarrierParams = (
            ftl_type: number,
            shipment_type: number,
            carrier: EmitCarrierDataParamsT
        ): EnterCarrierDataParamsT | null => {
            if (!state.ftlBid) return null

            if (ftl_type === 1 && shipment_type === 2) {
                return {
                    id: state.ftlBid.id,
                    carrier_id: carrier.carrier_id,
                }
            } else {
                return {
                    id: state.ftlBid.id,
                    ...carrier,
                }
            }
        }

        const enterCarrierDataSubmit = async (carrier: EmitCarrierDataParamsT) => {
            if (!state.ftlBid) return
            if (state.is_enter_carrier_data_loading) return

            try {
                state.is_enter_carrier_data_loading = true

                const params = getCarrierParams(
                    state.ftlBid.ftl_type,
                    state.ftlBid.shipment_type,
                    carrier
                )

                if (!params) return

                await ftl_bids.enterCarrierData(params as EnterCarrierDataParamsT)

                notifications.notify({
                    title: 'Данные о перевозчике заполнены',
                    type: 'success',
                })

                state.is_enter_carrier_data = false

                loadFtlBid()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При внесении данных о перевозчике произошла ошибка!',
                })
            } finally {
                state.is_enter_carrier_data_loading = false
            }
        }

        const enterCarrierDataCancel = () => {
            state.is_enter_carrier_data = false
        }

        const onEnterCostPrice = () => {
            state.is_enter_cost_price = true
        }

        const enterCostPriceSubmit = async (payload: EnterCostPricePayloadT) => {
            if (!state.ftlBid) return
            if (state.is_enter_cost_price_loading) return

            try {
                state.is_enter_cost_price_loading = true

                const params = {
                    id: state.ftlBid.id,
                    ...payload,
                }

                await ftl_bids.enterCostPrice(params as EnterCostPriceParamsT)

                notifications.notify({
                    title: 'Себестоимость внесена',
                    type: 'success',
                })

                state.is_enter_cost_price = false

                await changeStatusTo('on_approval')

                loadFtlBid()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При внесении себестоимости произошла ошибка!',
                })
            } finally {
                state.is_enter_cost_price_loading = false
            }
        }

        const enterCostPriceCancel = () => {
            state.is_enter_cost_price = false
        }

        const onEnterComment = () => {
            state.is_enter_comment = true
        }

        const enterCommentSubmit = async (lack_data_comment: string) => {
            if (!state.ftlBid) return
            if (state.is_enter_comment_loading) return

            const answer = confirm(`Вы подтверждаете, что данных недостаточно?`)
            if (!answer) return

            try {
                state.is_enter_comment_loading = true

                const params = {
                    lack_data_comment,
                }

                await ftl_bids.enterComment(state.ftlBid.id, params)

                notifications.notify({
                    title: 'Комментарий успешно добавлен',
                    type: 'success',
                })

                state.is_enter_comment = false

                loadFtlBid()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При внесении комментария произошла ошибка!',
                })
            } finally {
                state.is_enter_comment_loading = false
            }
        }

        const enterCommentCancel = () => {
            state.is_enter_comment = false
        }

        const onCancelBid = () => {
            const answer = confirm(`Вы действительно хотите отменить заявку?`)
            if (!answer) return

            state.is_cancel_bid = true
        }

        const cancelBidSubmit = async ({
            rejection_comment,
            rejection_reason_id,
        }: {
            rejection_comment: string
            rejection_reason_id: number
        }) => {
            try {
                await changeStatusTo('canceled', rejection_comment, rejection_reason_id)
                loadFtlBid()
            } catch (error) {
                console.log(error)
            }
        }

        const cancelBidCancel = () => {
            state.is_cancel_bid = false
        }

        const saveFile = (content: Blob, file_name: string) => {
            const download_url = window.URL.createObjectURL(content)

            const link = document.createElement('a')
            link.href = download_url
            link.setAttribute('download', file_name)
            document.body.appendChild(link)
            link.click()
            link.remove()
        }

        const onPrintInvoices = async () => {
            if (state.is_invoices_printing) return

            try {
                state.is_invoices_printing = true

                const response = await ftl_bids.printFtlBidInvoices(id)
                saveFile(response, `Накладные FTL заявки ${id}`)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При распечатке накладных произошла ошибка',
                })
            } finally {
                state.is_invoices_printing = false
            }
        }

        const sendD24 = async () => {
            if (
                state.is_document_loading ||
                !state.ftlBid ||
                !state.ftlBid.logist_bid ||
                !state.ftlBid.logist_bid.id
            ) {
                return
            }

            try {
                state.is_document_loading = true

                await ftl_bids.sendD24(state.ftlBid.logist_bid.id)
                notifications.notify({
                    title: 'Отправлен на подпись в договор Д24',
                    type: 'success',
                })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При распечатке накладных произошла ошибка',
                })
            } finally {
                state.is_document_loading = false
            }
        }

        const downloadLogistBid = async () => {
            if (
                state.is_download_loading ||
                !state.ftlBid ||
                !state.ftlBid.logist_bid ||
                !state.ftlBid.logist_bid.id
            ) {
                return
            }

            try {
                state.is_download_loading = true

                const response = await contracts.downloadLogistBid(state.ftlBid.logist_bid.id)
                saveFile(response, `Заявка логиста (FTL-заявки ID: ${id})`)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При скачивании произошла ошибка',
                })
            } finally {
                state.is_download_loading = false
            }
        }

        return {
            state,
            loadFtlBid,
            onAcceptInWork,
            onEnterCostToBePaid,
            enterCostToBePaidSubmit,
            enterCostToBePaidCancel,
            onRepeatCalculateCost,
            onEnterCarrierData,
            onBidAdded,
            enterCarrierDataSubmit,
            enterCarrierDataCancel,
            onEnterCostPrice,
            enterCostPriceSubmit,
            enterCostPriceCancel,
            onEnterComment,
            enterCommentSubmit,
            enterCommentCancel,
            onCancelBid,
            cancelBidSubmit,
            cancelBidCancel,
            onPrintInvoices,
            sendD24,
            downloadLogistBid,
        }
    },
})
